import { useEffect, useState } from 'react';
import { configApi } from '../api/client';
import type { SiteConfig } from '@bosamie/shared';
import { showToast } from '../components/ui';

export function Config() {
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [siteName, setSiteName] = useState('');
  const [viber, setViber] = useState('');
  const [phone, setPhone] = useState('');
  const [currency, setCurrency] = useState('MMK');
  const [promoStripText, setPromoStripText] = useState('');
  const [contactTip, setContactTip] = useState('');

  // PIN change state
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [savingPin, setSavingPin] = useState(false);

  useEffect(() => {
    configApi.get().then((data) => {
      setConfig(data);
      setSiteName(data.siteName);
      setViber(data.viber);
      setPhone(data.phone);
      setCurrency(data.currency);
      setPromoStripText(data.promoStripText || '');
      setContactTip(data.contactTip || '');
    }).catch(() => {
      showToast('加载失败');
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    if (!siteName.trim()) { showToast('站点名不能为空'); return; }
    if (!viber.trim()) { showToast('Viber ID 不能为空'); return; }
    if (!phone.trim()) { showToast('电话不能为空'); return; }

    setSaving(true);
    try {
      await configApi.update({
        siteName: siteName.trim(),
        viber: viber.trim(),
        phone: phone.trim(),
        currency,
        promoStripText: promoStripText || undefined,
        contactTip: contactTip || undefined,
      });
      showToast('保存成功');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePin = async () => {
    if (!/^\d{6}$/.test(newPin)) {
      showToast('PIN 必须为 6 位数字');
      return;
    }
    if (newPin !== confirmPin) {
      showToast('两次输入的 PIN 不一致');
      return;
    }
    setSavingPin(true);
    try {
      await configApi.update({ adminPin: newPin });
      showToast('PIN 修改成功');
      setNewPin('');
      setConfirmPin('');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'PIN 修改失败');
    } finally {
      setSavingPin(false);
    }
  };

  if (loading) {
    return <div className="card"><div className="skel" style={{ height: 400 }} /></div>;
  }

  return (
    <div className="card" style={{ maxWidth: 600 }}>
      <div className="card-header">
        <div className="card-title">信息配置</div>
      </div>

      <div className="form-group">
        <label className="form-label">站点名 <span className="req">*</span></label>
        <input className="form-input" value={siteName} onChange={(e) => setSiteName(e.target.value)} />
      </div>

      <div className="form-group">
        <label className="form-label">Viber ID <span className="req">*</span></label>
        <input className="form-input" value={viber} onChange={(e) => setViber(e.target.value)} placeholder="如 09888819989" />
      </div>

      <div className="form-group">
        <label className="form-label">电话 <span className="req">*</span></label>
        <input className="form-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="如 09970608008" />
      </div>

      <div className="form-group">
        <label className="form-label">货币</label>
        <select className="form-select" value={currency} onChange={(e) => setCurrency(e.target.value)}>
          <option value="MMK">MMK (缅币)</option>
          <option value="USD">USD (美元)</option>
          <option value="THB">THB (泰铢)</option>
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">首页促销条文案</label>
        <input className="form-input" value={promoStripText} onChange={(e) => setPromoStripText(e.target.value)} placeholder="选填，如 Handbags | Factory direct prices" />
      </div>

      <div className="form-group">
        <label className="form-label">联系页提示文案</label>
        <textarea className="form-textarea" value={contactTip} onChange={(e) => setContactTip(e.target.value)} placeholder="选填，如 Send the model code via Viber to place your order." />
      </div>

      <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? '保存中...' : '保存'}
        </button>
      </div>

      {/* 修改 PIN */}
      <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--line)' }}>
        <div className="card-title" style={{ marginBottom: 16 }}>修改管理员 PIN</div>
        <div className="form-group">
          <label className="form-label">新 PIN（6 位数字）</label>
          <input
            className="form-input"
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="输入 6 位数字"
            maxLength={6}
            type="password"
          />
        </div>
        <div className="form-group">
          <label className="form-label">确认新 PIN</label>
          <input
            className="form-input"
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="再次输入 6 位数字"
            maxLength={6}
            type="password"
          />
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>
          <button
            className="btn btn-primary"
            onClick={handleSavePin}
            disabled={savingPin || !newPin}
          >
            {savingPin ? '修改中...' : '修改 PIN'}
          </button>
        </div>
      </div>
    </div>
  );
}
